import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { PrivacyCheckbox, PrivacyFirstLayer } from "@/components/LegalLinks";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refFromUrl = params.get("ref") || "";
  const emailFromUrl = params.get("email") || "";
  const [form, setForm] = useState({ full_name: "", email: emailFromUrl, password: "", business_name: "", phone: "", referral_code: refFromUrl });
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Contraseña mínima 6 caracteres");
    if (!accepted) return toast.error("Debes aceptar la Política de Privacidad");
    setLoading(true);
    try {
      const u = await register({ ...form, accept_privacy: true });
      // Paywall: new users go to billing first to subscribe (Stripe handles 14-day trial)
      if (u?.has_access === false || u?.subscription_status === "pending_payment") {
        toast.success("Cuenta creada. Activa tu suscripción para empezar.");
        nav("/billing?welcome=1");
      } else {
        toast.success("Cuenta creada. ¡Bienvenido!");
        nav("/dashboard");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al registrarse");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-1 bg-orange-600 text-white items-end p-12">
        <div>
          <Link to="/" className="font-display font-extrabold text-2xl tracking-tight">AutoPresupuestos<span className="text-slate-900">.</span></Link>
          <h2 className="font-display font-extrabold text-4xl mt-12 leading-tight">14 días gratis.<br/>Cancela cuando<br/>quieras.</h2>
          <p className="mt-6 text-white/80 text-sm">Básico 29 €/mes · PRO 39 €/mes. Solo se cobra después del trial.</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={onSubmit} className="w-full max-w-md">
          <div className="kbar mb-2">Crear cuenta</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Empieza en 60 segundos</h1>
          <p className="text-slate-500 mt-2 text-sm">¿Ya tienes cuenta? <Link to="/login" className="text-orange-600 font-semibold">Inicia sesión</Link></p>

          <div className="grid grid-cols-1 gap-4 mt-8">
            <Field label="Nombre completo" testid="reg-name" value={form.full_name} onChange={set("full_name")} required />
            <Field label="Email" type="email" testid="reg-email" value={form.email} onChange={set("email")} required />
            <Field label="Contraseña" type="password" testid="reg-password" value={form.password} onChange={set("password")} required />
            <Field label="Nombre del negocio (opcional)" testid="reg-business" value={form.business_name} onChange={set("business_name")} />
            <Field label="Teléfono (opcional)" testid="reg-phone" value={form.phone} onChange={set("phone")} />
            <Field label="Código de invitación (opcional)" testid="reg-ref" value={form.referral_code} onChange={set("referral_code")} placeholder="ABC123" />
            {refFromUrl && (
              <div className="-mt-2 px-3 py-2 bg-orange-50 border border-orange-200 text-xs text-orange-800" data-testid="ref-hint">
                ¡Te invitó alguien! Os llevaréis <strong>1 mes gratis</strong> al suscribiros.
              </div>
            )}
          </div>

          <div className="mt-6 space-y-3">
            <PrivacyCheckbox checked={accepted} onChange={setAccepted} testid="reg-privacy-checkbox" />
            <PrivacyFirstLayer />
          </div>

          <button data-testid="reg-submit" disabled={loading || !accepted} className="btn-brand h-12 w-full mt-6 inline-flex items-center justify-center disabled:opacity-50">
            {loading ? "Creando cuenta…" : "Crear cuenta y suscribirme"}
          </button>
          <p className="mt-3 text-xs text-slate-500 text-center">Te llevamos al checkout. <strong>14 días gratis</strong>, después Básico 29€/mes o PRO 39€/mes. Cancela en 1 clic.</p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, type = "text", testid, ...rest }) {
  return (
    <div>
      <label className="kbar block">{label}</label>
      <input
        data-testid={testid}
        type={type}
        className="w-full h-12 mt-2 px-3 border border-slate-200 focus:border-orange-600 focus:ring-2 focus:ring-orange-100 outline-none"
        {...rest}
      />
    </div>
  );
}
